<template>
  <div class="login-page fill-height">
    <v-row no-gutters class="login-row">
      <v-col cols="12" md="6" class="login-brand pa-8 pa-md-12 d-none d-md-flex flex-column justify-center">
        <div class="login-brand__inner">
          <v-icon color="white" size="56" class="login-brand__icon">mdi-lock-reset</v-icon>
          <h1 class="login-brand__title">Recuperar acceso</h1>
          <p class="login-brand__subtitle">
            Si ya tienes correo personal verificado te enviamos una temporal.
            Si aún no lo registraste, aquí mismo lo das de alta y lo verificas.
          </p>
        </div>
      </v-col>

      <v-col cols="12" md="6" class="login-form-col pa-6 pa-sm-10 pa-md-12 d-flex align-center justify-center">
        <div class="login-form-wrap">
          <div class="text-center mb-8">
            <v-img
              src="@/assets/logo.png"
              max-height="52"
              contain
              class="mx-auto login-logo"
              alt="School of English"
            />
          </div>

          <header class="text-center mb-6">
            <h2 class="login-heading">{{ tituloPaso }}</h2>
            <p class="login-lead">{{ leadPaso }}</p>
          </header>

          <v-alert v-if="formError" type="error" dense outlined class="mb-4">
            {{ formError }}
          </v-alert>

          <v-alert v-if="successMsg" type="success" dense outlined class="mb-4">
            {{ successMsg }}
            <div v-if="debugTempPassword" class="mt-3 pa-3" style="background:#e8f5e9;border-radius:8px;">
              <div class="caption mb-1">Contraseña temporal (ambiente de pruebas)</div>
              <div class="text-h5 font-weight-bold">{{ debugTempPassword }}</div>
              <div class="caption mt-2">
                Cópiala, ve al login e inicia sesión. Luego el sistema te pedirá cambiarla.
              </div>
            </div>
          </v-alert>

          <!-- Paso 1: identificar usuario -->
          <v-form v-if="!successMsg && step === 'login'" @submit.prevent="continuarLogin">
            <label class="login-label">Usuario / correo institucional / correo personal</label>
            <v-text-field
              v-model="login"
              placeholder="usuario, correo institucional o personal verificado"
              outlined
              dense
              hide-details="auto"
              autocomplete="username"
              prepend-inner-icon="mdi-account-outline"
              class="mb-4"
              :disabled="cargar"
            />

            <v-btn
              type="submit"
              block
              depressed
              x-large
              color="primary"
              class="login-submit text-none font-weight-semibold mt-2"
              :loading="cargar"
              :disabled="cargar || !login"
            >
              Continuar
            </v-btn>
          </v-form>

          <!-- Paso 2: registrar correo personal (sin sesión) -->
          <v-form v-else-if="!successMsg && step === 'email'" @submit.prevent="enviarCodigo">
            <v-alert dense outlined type="info" class="mb-4">
              Usuario: <strong>{{ login }}</strong>. Ingresa un correo personal
              al que tengas acceso (Gmail, Outlook, etc.).
            </v-alert>

            <label class="login-label">Correo personal</label>
            <v-text-field
              v-model="emailPersonal"
              type="email"
              placeholder="tucorreo@gmail.com"
              outlined
              dense
              hide-details="auto"
              autocomplete="email"
              prepend-inner-icon="mdi-email-outline"
              class="mb-4"
              :disabled="cargar"
            />

            <v-btn
              type="submit"
              block
              depressed
              x-large
              color="primary"
              class="login-submit text-none font-weight-semibold mt-2"
              :loading="cargar"
              :disabled="cargar || !emailValido"
            >
              Enviar código
            </v-btn>

            <div class="text-center mt-3">
              <v-btn text small color="primary" class="text-none" :disabled="cargar" @click="volverLogin">
                Cambiar usuario
              </v-btn>
            </div>
          </v-form>

          <!-- Paso 3: verificar código -->
          <v-form v-else-if="!successMsg && step === 'code'" @submit.prevent="verificarCodigo">
            <v-alert dense outlined type="info" class="mb-4">
              <div v-if="debugCode">
                Ambiente de pruebas: tu código es
                <strong class="text-h6">{{ debugCode }}</strong>
              </div>
              <div v-else>
                Enviamos un código a <strong>{{ emailPersonal }}</strong>.
                Al confirmarlo te mandaremos la contraseña temporal al mismo correo.
              </div>
            </v-alert>

            <label class="login-label">Código de 6 dígitos</label>
            <v-text-field
              v-model="code"
              outlined
              dense
              hide-details="auto"
              maxlength="6"
              prepend-inner-icon="mdi-shield-key-outline"
              class="mb-4"
              :disabled="cargar"
            />

            <v-btn
              type="submit"
              block
              depressed
              x-large
              color="primary"
              class="login-submit text-none font-weight-semibold mt-2"
              :loading="cargar"
              :disabled="cargar || !codigoValido"
            >
              Verificar y enviar temporal
            </v-btn>

            <div class="text-center mt-3">
              <v-btn text small color="primary" class="text-none" :disabled="cargar" @click="step = 'email'">
                Cambiar correo
              </v-btn>
            </div>
          </v-form>

          <p class="login-footnote text-center mt-8 mb-0">
            <router-link :to="{ name: 'Login' }" class="login-link">
              Volver al inicio de sesión
            </router-link>
          </p>
        </div>
      </v-col>
    </v-row>

    <carga v-if="cargar" />
  </div>
</template>

<script>
import carga from '@/components/alertas/carga.vue'

export default {
  name: 'OlvideContrasena',

  components: { carga },

  data() {
    return {
      step: 'login',
      login: '',
      emailPersonal: '',
      code: '',
      debugCode: '',
      debugTempPassword: '',
      formError: '',
      successMsg: '',
      cargar: false,
    }
  },

  computed: {
    emailValido() {
      const e = String(this.emailPersonal || '').trim()
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)
    },
    codigoValido() {
      return /^\d{6}$/.test(String(this.code || '').trim())
    },
    tituloPaso() {
      if (this.step === 'email') return 'Registra tu correo personal'
      if (this.step === 'code') return 'Verifica tu correo'
      return 'Olvidé mi contraseña'
    },
    leadPaso() {
      if (this.step === 'email') {
        return 'Como aún no tenías correo de recuperación, debes registrarlo ahora'
      }
      if (this.step === 'code') {
        return 'Confirma el código para ligar el correo y recibir la temporal'
      }
      return 'Ingresa tu usuario, correo institucional o el correo personal ya verificado'
    },
  },

  methods: {
    volverLogin() {
      this.step = 'login'
      this.emailPersonal = ''
      this.code = ''
      this.debugCode = ''
      this.debugTempPassword = ''
      this.formError = ''
      this.successMsg = ''
    },

    handleHttpError(error) {
      const status = error.response && error.response.status
      if (status === 429) {
        this.formError =
          (error.response.data && error.response.data.message) ||
          'Demasiados intentos. Espera unos minutos.'
        return
      }
      this.formError =
        (error.response && error.response.data && error.response.data.message) ||
        'No fue posible procesar la solicitud. Intenta de nuevo.'
    },

    continuarLogin() {
      this.formError = ''
      this.successMsg = ''
      this.debugTempPassword = ''
      const value = String(this.login || '').trim()
      if (!value) {
        this.formError = 'Ingresa tu usuario, correo institucional o correo personal verificado.'
        return
      }

      this.cargar = true
      this.$http
        .post('password.forgot', { email: value })
        .then((response) => {
          const data = response.data || {}
          if (data.code === 'NEEDS_RECOVERY_EMAIL') {
            this.step = 'email'
            this.formError = ''
            return
          }
          this.debugTempPassword = data.debug_temp_password || ''
          this.successMsg =
            data.message ||
            'Si el usuario existe y tiene correo de recuperación verificado, recibirás una contraseña temporal.'
        })
        .catch((error) => this.handleHttpError(error))
        .finally(() => {
          this.cargar = false
        })
    },

    enviarCodigo() {
      this.formError = ''
      if (!this.emailValido) {
        this.formError = 'Ingresa un correo personal válido.'
        return
      }

      this.cargar = true
      this.$http
        .post('password.forgot.email.save', {
          email_login: String(this.login).trim(),
          email_recuperacion: String(this.emailPersonal).trim().toLowerCase(),
        })
        .then((response) => {
          const data = response.data || {}
          this.emailPersonal = data.email_recuperacion || this.emailPersonal
          this.debugCode = data.debug_code || ''
          this.code = this.debugCode || ''
          this.step = 'code'
        })
        .catch((error) => this.handleHttpError(error))
        .finally(() => {
          this.cargar = false
        })
    },

    verificarCodigo() {
      this.formError = ''
      if (!this.codigoValido) {
        this.formError = 'El código debe ser de 6 dígitos.'
        return
      }

      this.cargar = true
      this.$http
        .post('password.forgot.email.verify', {
          email_login: String(this.login).trim(),
          code: String(this.code).trim(),
        })
        .then((response) => {
          const data = response.data || {}
          this.debugTempPassword = data.debug_temp_password || ''
          this.successMsg =
            data.message ||
            'Te enviamos una contraseña temporal. Inicia sesión y cámbiala.'
          this.step = 'login'
        })
        .catch((error) => this.handleHttpError(error))
        .finally(() => {
          this.cargar = false
        })
    },
  },
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: #f5f7fb;
}

.login-row {
  min-height: 100vh;
}

.login-brand {
  background: linear-gradient(145deg, #0d47a1 0%, #1565c0 55%, #1e88e5 100%);
  color: #fff;
}

.login-brand__title {
  font-size: 2rem;
  font-weight: 700;
  margin: 16px 0 8px;
}

.login-brand__subtitle {
  opacity: 0.9;
  max-width: 360px;
}

.login-form-wrap {
  width: 100%;
  max-width: 420px;
}

.login-heading {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a237e;
}

.login-lead {
  color: #607d8b;
  margin: 4px 0 0;
}

.login-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #546e7a;
  margin-bottom: 6px;
}

.login-submit {
  letter-spacing: 0.02em;
}

.login-footnote {
  color: #78909c;
  font-size: 0.875rem;
}

.login-link {
  color: #1565c0;
  text-decoration: none;
  font-weight: 600;
  margin-left: 4px;
}

.login-link:hover {
  text-decoration: underline;
}
</style>
